import {
  YEARLY_PRODUCTS,
  MONTHLY_PRODUCTS,
  TEAM_PRODUCTS,
  ProductPath,
  getAllPricesByCountryCurrency,
} from "./fastspring";
import type { Subscription } from "../store";
import regionalPricingConfig from "../data/regional-pricing.json";
import type { AstroGlobal } from "astro";

export const getPlanTitle = (product: ProductPath) => {
  switch (true) {
    case isTeamPlan(product) === true:
      return "Team Annual Plan";
    case isYearlyPlan(product) === true:
      return "Annual Plan";
    default:
      return "Monthly Plan";
  }
};

export const isMonthlyPlan = (product: ProductPath) => {
  return MONTHLY_PRODUCTS.includes(product);
};

export const isYearlyPlan = (product: ProductPath) => {
  return YEARLY_PRODUCTS.includes(product);
};

export const isTeamPlan = (product: ProductPath) => {
  return TEAM_PRODUCTS.includes(product);
};

export type Tier = "Pro" | "Plus" | "Lite" | "Edu";
export type Term = "Monthly" | "Annual";

export enum TierPriorityMap {
  "Pro" = 3,
  "Plus" = 2,
  "Lite" = 1,
  "Edu" = 0,
}

export const mapProductToTier = (product: ProductPath): Tier | undefined => {
  switch (product) {
    case ProductPath.PRO_MONTHLY:
    case ProductPath.PRO_ANNUAL:
    case ProductPath.PRO_MONTHLY_REGION1:
    case ProductPath.PRO_MONTHLY_REGION1_MANUAL:
    case ProductPath.PRO_ANNUAL_REGION1:
    case ProductPath.PRO_TEAM:
      return "Pro";
    case ProductPath.PLUS_MONTHLY:
    case ProductPath.PLUS_ANNUAL:
    case ProductPath.PLUS_MONTHLY_REGION1:
    case ProductPath.PLUS_MONTHLY_REGION1_MANUAL:
    case ProductPath.PLUS_ANNUAL_REGION1:
      return "Plus";
    case ProductPath.LITE_MONTHLY:
    case ProductPath.LITE_ANNUAL:
    case ProductPath.LITE_MONTHLY_REGION1:
    case ProductPath.LITE_ANNUAL_REGION1:
      return "Lite";
    default:
      return;
  }
};

export const mapProductToTerm = (product: ProductPath): Term | undefined => {
  switch (true) {
    case isMonthlyPlan(product):
    case product.includes("monthly"):
      return "Monthly";
    case isYearlyPlan(product):
    case isTeamPlan(product):
    case product.includes("yearly"):
    case product.includes("annual"):
      return "Annual";
    default:
      return;
  }
};
export const mapSkuToTerm = (product: string): Term | undefined => {
  switch (true) {
    case product.includes("monthly"):
      return "Monthly";
    case product.includes("yearly"):
    case product.includes("annual"):
      return "Annual";
    default:
      return;
  }
};

export const mapSkuToTier = (sku: string): Tier | undefined => {
  switch (true) {
    case sku.includes("_pro_"):
      return "Pro";
    case sku.includes("_plus_"):
      return "Plus";
    case sku.includes("_lite_"):
      return "Lite";
    case sku.includes("_edu_"):
      return "Edu";
    default:
      return;
  }
};

export const findTopCurrentSubscription = (subs: Subscription[]) => {
  if (subs.length === 0) return undefined;

  const activeSubs = subs.filter((sub) => sub.status === "Active");
  if (activeSubs.length === 0) return;
  if (activeSubs.length === 1) return activeSubs[0];

  return activeSubs.reduce((highest, sub) => {
    const currentLicenceType = mapSkuToTier(sub.sku);
    const highestLicenceType = mapSkuToTier(highest.sku);
    const currentSubPriority = currentLicenceType
      ? TierPriorityMap[currentLicenceType]
      : 0;
    const highestSubPriority = highestLicenceType
      ? TierPriorityMap[highestLicenceType]
      : 0;
    return currentSubPriority > highestSubPriority ? sub : highest;
  });
};

export const getProductPathsByCountry = (
  countryCode: string,
  Astro: AstroGlobal,
) => {
  const isRegionalPricingEnabled = regionalPricingConfig.includes(countryCode);

  let PRO_MONTHLY_PATH = ProductPath.PRO_MONTHLY;
  if (isRegionalPricingEnabled) {
    PRO_MONTHLY_PATH = ProductPath.PRO_MONTHLY_REGION1;
  }

  const PRO_ANNUAL_PATH = isRegionalPricingEnabled
    ? ProductPath.PRO_ANNUAL_REGION1
    : ProductPath.PRO_ANNUAL;

  let PLUS_MONTHLY_PATH = ProductPath.PLUS_MONTHLY;
  if (isRegionalPricingEnabled) {
    PLUS_MONTHLY_PATH = ProductPath.PLUS_MONTHLY_REGION1;
  }

  const PLUS_ANNUAL_PATH = isRegionalPricingEnabled
    ? ProductPath.PLUS_ANNUAL_REGION1
    : ProductPath.PLUS_ANNUAL;

  const LITE_MONTHLY_PATH = isRegionalPricingEnabled
    ? ProductPath.LITE_MONTHLY_REGION1
    : ProductPath.LITE_MONTHLY;

  const LITE_ANNUAL_PATH = isRegionalPricingEnabled
    ? ProductPath.LITE_ANNUAL_REGION1
    : ProductPath.LITE_ANNUAL;

  const productPaths = {
    PRO_MONTHLY_PATH,
    PRO_ANNUAL_PATH,
    PLUS_MONTHLY_PATH,
    PLUS_ANNUAL_PATH,
    LITE_MONTHLY_PATH,
    LITE_ANNUAL_PATH,
  };

  return productPaths;
};

export const getPricingDataByCountry = async (
  countryCode: string,
  currency: string,
  Astro: AstroGlobal,
) => {
  const productPaths = getProductPathsByCountry(countryCode, Astro);

  const allPrices = await getAllPricesByCountryCurrency(countryCode, currency);

  const prices = allPrices.products.filter((price) =>
    Object.values(productPaths).includes(price.product),
  );

  const proMonthlyPriceData = prices.find(
    (price) => price.product === productPaths.PRO_MONTHLY_PATH,
  );
  const proAnnualPriceData = prices.find(
    (price) => price.product === productPaths.PRO_ANNUAL_PATH,
  );
  const plusMonthlyPriceData = prices.find(
    (price) => price.product === productPaths.PLUS_MONTHLY_PATH,
  );
  const plusAnnualPriceData = prices.find(
    (price) => price.product === productPaths.PLUS_ANNUAL_PATH,
  );
  const liteMonthlyPriceData = prices.find(
    (price) => price.product === productPaths.LITE_MONTHLY_PATH,
  );
  const liteAnnualPriceData = prices.find(
    (price) => price.product === productPaths.LITE_ANNUAL_PATH,
  );

  return {
    proMonthlyPriceData,
    proAnnualPriceData,
    plusMonthlyPriceData,
    plusAnnualPriceData,
    liteMonthlyPriceData,
    liteAnnualPriceData,
  };
};
